<template>
  <div class="ai-settings">
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">
          <Sparkles :size="20" class="me-2" />
          Groq AI Configuration
        </h5>
      </div>
      <div class="card-body">
        <div v-if="loadState.IsLoading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-2"></i> Loading settings...
        </div>
        <form v-else @submit.prevent="saveSettings">
          <div class="alert alert-info mb-4">
            <i class="fas fa-info-circle me-2"></i>
            Configure your
            <a href="https://console.groq.com/keys" target="_blank">Groq API key</a>
            to enable AI-assisted features (e.g. invoice text generation).
            Groq offers a free tier with OpenAI-compatible endpoints.
          </div>

          <!--
          <div class="row">
            <div class="col-md-12">
              {{ settings }}
            </div>
          </div>
          -->
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">API Key</label>
              <div class="input-group">
                <input
                  :type="showApiKey ? 'text' : 'password'"
                  class="form-control"
                  v-model="settings.apiKey"
                  placeholder="gsk_..."
                  autocomplete="off"
                />
                <button
                  type="button"
                  class="btn bg-transparent border border-start-0"
                  @click.prevent="showApiKey = !showApiKey"
                  tabindex="-1"
                >
                  <Eye v-if="!showApiKey" :size="18" />
                  <EyeOff v-else :size="18" />
                </button>
              </div>
              <small class="text-muted">Your Groq API key</small>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Base URL</label>
              <input
                type="text"
                class="form-control"
                v-model="settings.baseUrl"
                placeholder="https://api.groq.com/openai/v1"
              />
              <small class="text-muted">Groq OpenAI-compatible API endpoint</small>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Default Model</label>
              <input
                type="text"
                class="form-control"
                v-model="settings.defaultModel"
                placeholder="openai/gpt-oss-120b"
              />
              <small class="text-muted">
                Model id used when none is specified.
                <a href="#" @click.prevent="loadModels" v-if="settings.apiKey">List available models</a>
              </small>
              <div v-if="models.length > 0" class="mt-2">
                <select class="form-select form-select-sm" v-model="settings.defaultModel">
                  <option v-for="m in models" :key="m.Id" :value="m.Id">{{ m.Id }}</option>
                </select>
              </div>
            </div>
            <div class="col-md-6 mb-3 d-flex align-items-end">
              <div class="form-check mb-2">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="enableAi"
                  v-model="settings.enabled"
                />
                <label class="form-check-label" for="enableAi">
                  <strong>Enable AI Features</strong>
                </label>
                <small class="text-muted d-block">
                  Allow AI-assisted actions (invoice text, etc.) in the app
                </small>
              </div>
            </div>
          </div>

          <div v-if="modelsState.IsError" class="alert alert-warning py-2">
            <i class="fas fa-exclamation-triangle me-1"></i>
            Could not load models. Check your API key and try the Test Connection button.
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="testConnection"
              :disabled="testState.IsLoading || !settings.apiKey"
            >
              <Zap :size="16" class="me-1" />
              {{ testState.IsLoading ? 'Testing...' : 'Test Connection' }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saveState.IsLoading">
              <Save :size="16" class="me-1" />
              {{ saveState.IsLoading ? 'Saving...' : 'Save Settings' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { Sparkles, Save, Zap, Eye, EyeOff } from 'lucide-vue-next';
import OptionsService from '@/services/OptionsService';
import type { GroqSettings } from '@/services/OptionsService';
import ApiService from '@/services/ApiService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import { useApiRequest } from '@/composables';

const { state: loadState, execute: executeLoad } = useApiRequest<GroqSettings>('ai:load');
const { state: saveState, execute: executeSave } = useApiRequest('ai:save');
const { state: testState, execute: executeTest } = useApiRequest('ai:test');
const { state: modelsState, execute: executeModels } = useApiRequest<any[]>('ai:models');

const settings = ref<GroqSettings>({
  apiKey: '',
  baseUrl: 'https://api.groq.com/openai/v1',
  defaultModel: 'openai/gpt-oss-120b',
  enabled: false
});

const models = ref<Array<{ Id: string }>>([]);
const showApiKey = ref(false);

const loadSettings = async () => {
  await executeLoad(() => OptionsService.getGroqSettings());
  if (loadState.value.IsError) {
    Alert.notify({ text: 'Failed to load AI settings', type: 'error' });
    return;
  }
  if (loadState.value.Data) {
    settings.value = { ...loadState.value.Data };
  }
};

const saveSettings = async () => {
  await executeSave(() => OptionsService.saveGroqSettings(settings.value));
  if (saveState.value.IsError) {
    Alert.notify({ text: 'Failed to save AI settings', type: 'error' });
  } else {
    Toast.success('AI settings saved successfully!');
  }
};

const testConnection = async () => {
  // Save first so the backend has the API key to test with
  await executeSave(() => OptionsService.saveGroqSettings(settings.value));
  if (saveState.value.IsError) {
    Alert.notify({ text: 'Failed to save settings before test', type: 'error' });
    return;
  }
  await executeTest(() => ApiService.testAiConnection());
  if (testState.value.IsError) {
    Alert.notify({ text: 'Failed to connect to Groq. Check your API key.', type: 'error' });
  } else {
    Toast.success('Groq connection successful!');
  }
};

const loadModels = async () => {
  // Save first so the backend has the API key
  await executeSave(() => OptionsService.saveGroqSettings(settings.value));
  if (saveState.value.IsError) {
    Alert.notify({ text: 'Failed to save settings before loading models', type: 'error' });
    return;
  }
  await executeModels(() => ApiService.getAiModels());
  if (modelsState.value.IsError) {
    Alert.notify({ text: 'Failed to load models', type: 'error' });
  } else if (modelsState.value.Data) {
    models.value = modelsState.value.Data;
    Toast.success(`Loaded ${models.value.length} available models`);
  }
};

onMounted(() => {
  loadSettings();
});
</script>
